import React, { useState, useContext, useEffect } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import PasswordTextField from "components/PasswordTextField";
import { ISXContext } from "services/Utils";

const ForgotPasswordDialog = (props) => {
  const [username, setUsername] = useState("");

  const handleResetpass = () => {
    props.handleResetpass(username);
    setUsername("");
    props.handleClose();
  };

  const handleClose = () => {
    setUsername("");
    props.handleClose();
  };

  return (
    <Dialog open={props.open}>
      <DialogTitle>Forgot Password?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please enter your user name; you will be sent a link that will allow
          you to change your password.
        </DialogContentText>
        <TextField
          variant="standard"
          fullWidth
          label="Username"
          type="email"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleResetpass()}
          inputProps={{
            autoCapitalize: "none",
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleResetpass}>
          Request Link
        </Button>
        <Button onClick={handleClose} style={{ color: "red" }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function buttonName(m) {
  return m.display_name ? m.display_name : "Login with " + m.name;
}

const Login = (props) => {
  const [mfaRequested, setMfaRequested] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [passwordResetRequestSent, setPasswordResetRequestSent] =
    useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const isxContext = useContext(ISXContext);

  useEffect(() => {
    document.title = `Login | ${isxContext.labels.organization}`;
  }, [isxContext.labels.organization]);

  const requestResendMfa = () => {
    props.attemptLogin(true);
    setMfaRequested(true);
    props.clearMFA();
  };

  const handleResetpass = (username) => {
    props.handleResetpass(username);
    setPasswordResetRequestSent(true);
  };

  const isInitialLogin = !passwordResetRequestSent && !props.mfa;

  return (
    <Grid
      container
      sm={12}
      mdOffset={3}
      md={6}
      lgOffset={4}
      lg={4}
      spacing={3}
      sx={{ width: "100%", p: 1, pt: 7, pb: 2.5, mx: 0 }}
    >
      <Grid xs={12}>
        <Typography variant="h6">{`${isxContext.labels.deviceCapitalized} Dashboard Login`}</Typography>
      </Grid>

      {isInitialLogin && (
        <>
          <Grid xs={12}>
            <TextField
              fullWidth
              variant="standard"
              label="Username"
              type="email"
              onChange={props.handleUsername}
              onKeyPress={(e) => e.key === "Enter" && props.attemptLogin()}
              inputProps={{
                autoCapitalize: "none",
              }}
              disabled={props.disabled || !!props.methods || props.showPassword}
            />
          </Grid>
          <Grid container spacing={3} xs={12}>
            {props.methods &&
              !props.showPassword &&
              props.methods.map((m) => (
                <Grid key={m.name}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={props.disabled}
                    key={m.name}
                    onClick={() => props.chooseMethod(m)}
                  >
                    {buttonName(m)}
                  </Button>
                </Grid>
              ))}
          </Grid>
          <Grid xs={12} style={!props.showPassword ? { display: "none" } : {}}>
            <PasswordTextField
              fullWidth
              label="Password"
              onChange={props.handlePassword}
              onKeyPress={(e) => e.key === "Enter" && props.attemptLogin()}
              disabled={props.disabled}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />
          </Grid>
          {!props.mfa &&
            (!props.methods || props.showPassword) &&
            !passwordResetRequestSent && (
              <Grid>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => props.attemptLogin()}
                  disabled={props.disabled}
                >
                  Login
                </Button>
              </Grid>
            )}
          <Grid xs={12}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              {props.showPassword && (
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    setResetPasswordDialogOpen(true);
                  }}
                >
                  forgot password?
                </Button>
              )}
            </div>
          </Grid>
        </>
      )}
      {props.mfa && (
        <>
          <Grid xs={12}>
            <Typography>
              Enter the security code provided via SMS, email, or authenticator
              app.
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              label="Security code"
              value={props.mfactor}
              onChange={props.handleMFA}
              onKeyPress={(e) => e.key === "Enter" && props.attemptLogin()}
              disabled={props.disabled}
            />
            <div>
              <Checkbox checked={props.msecure} onChange={props.handleSecure} />
              Remember me on this device; only check the box if the device is
              secure
            </div>
          </Grid>
          <Grid xs={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                style={{ marginRight: "1.5rem" }}
                onClick={requestResendMfa}
                disabled={props.disabled}
              >
                Resend Code
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => props.attemptLogin()}
                disabled={props.disabled || (props.mfa && !props.mfactor)}
              >
                Login with Code
              </Button>
            </div>
          </Grid>
        </>
      )}
      {(props.showPassword || props.methods) && (
        <Grid xs={12}>
          <Button
            variant="text"
            size="small"
            color="primary"
            sx={{ textTransform: "none" }}
            onClick={() => props.cancelLogin()}
          >
            Start again to Login
          </Button>
        </Grid>
      )}
      {passwordResetRequestSent && (
        <Grid xs={12}>
          <Typography>
            An email with a link to change your pasword has been sent to the
            email or verified SMS registered with your account.
          </Typography>
        </Grid>
      )}

      {props.error && (
        <Grid xs={12}>
          <Typography color="error" align="right">
            {props.error}
          </Typography>
        </Grid>
      )}
      {mfaRequested && (
        <Grid xs={12}>
          <Typography color="primary" align="right">
            Security code resent
          </Typography>
        </Grid>
      )}

      <ForgotPasswordDialog
        open={resetPasswordDialogOpen}
        handleClose={() => {
          setResetPasswordDialogOpen(false);
        }}
        handleUsername={props.handleUsername}
        handleResetpass={handleResetpass}
      />
    </Grid>
  );
};

export default Login;
